import type { Scenario } from '../game/types';
import { partnerForTool, toolById } from '../game/partners';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';
import { SiteFooter } from './SiteFooter';

interface Props {
  scenario: Scenario;
  onStart: () => void;
  /** Back to the case list, for someone who opened this and changed their mind. */
  onBack: () => void;
}

/**
 * Pre-game briefing.
 *
 * The legend is not hand-holding -- it is the point. Real tools encode meaning
 * in small visual conventions, and half of "spotting a misconfiguration" is
 * knowing how to read the instrument. Teach the vocabulary, then withhold the
 * answers.
 */
export function Briefing({ scenario, onStart, onBack }: Props) {
  return (
    <div className="panel">
      <RTNav />
      <div className="panel__inner">
        <BrandHeader />
        <h1>DevTool Detective</h1>
        <p className="lede">
          A sprint board has been handed to you mid-flight. Somebody has made a
          mess of it. Find out where.
        </p>

        <p className="source-line">
          A recreation of a <b>{toolById(scenario.source.toolId).name}</b>{' '}
          {scenario.source.screen.toLowerCase()}, built in HTML — it contains no{' '}
          {partnerForTool(scenario.source.toolId).name} code or assets.{' '}
          {toolById(scenario.source.toolId).name} is a product of{' '}
          {partnerForTool(scenario.source.toolId).name}. This game is not
          affiliated with or endorsed by{' '}
          {partnerForTool(scenario.source.toolId).name}.{' '}
          <a href="#attribution">Attribution</a>
        </p>

        <h2>How to play</h2>
        <ul className="rules">
          <li>
            <b>Tap anything that looks wrong.</b> Cards, column headers, and the
            sprint header at the top are all flaggable. Tap again to unflag.
          </li>
          <li>
            <b>Not every problem is on a card.</b> Some belong to a column, and
            one belongs to the sprint itself.
          </li>
          <li>
            <b>Everything you need is on the board.</b> The ⓘ button opens full
            details, but it will only ever confirm what the card already shows.
          </li>
          <li>
            <b>Wrong accusations cost you.</b> Flagging something that is fine
            loses points. Some of this board is perfectly healthy.
          </li>
        </ul>

        <h2>Reading the board</h2>
        <ul className="rules">
          <li>
            <span className="avatar avatar--inactive" style={{ background: '#a54800', display: 'inline-grid', verticalAlign: 'middle', marginRight: 10 }}>
              RT
            </span>
            <b style={{ display: 'inline' }}>Red ring</b> — the assignee's
            account has been deactivated.
          </li>
          <li>
            <span className="avatar avatar--none" style={{ display: 'inline-grid', verticalAlign: 'middle', marginRight: 10 }}>
              ?
            </span>
            <b style={{ display: 'inline' }}>Dashed circle</b> — nobody is
            assigned.
          </li>
          <li>
            <span className="points" style={{ marginRight: 10, verticalAlign: 'middle' }}>
              5
            </span>
            <span className="points points--hours" style={{ marginRight: 10, verticalAlign: 'middle' }}>
              16h
            </span>
            <span className="points points--none" style={{ marginRight: 10, verticalAlign: 'middle' }}>
              –
            </span>
            <b style={{ display: 'inline' }}>Estimate</b> — grey is story points,
            the board's configured unit. Amber is some other unit. Dashed means
            unestimated.
          </li>
          <li>
            <span className="column__wip column__wip--breached" style={{ marginRight: 10 }}>
              5 / 3
            </span>
            <b style={{ display: 'inline' }}>WIP badge</b> — cards in the column
            against its configured limit. Red means the limit is breached.
          </li>
          <li>
            <span className="idle idle--stale" style={{ marginRight: 10 }}>
              94d
            </span>
            <b style={{ display: 'inline' }}>Idle time</b> — days since the ticket
            last moved. Red past thirty days.
          </li>
        </ul>

        <p className="lede">
          {scenario.defects.length} problems are waiting on the{' '}
          <strong>{scenario.sprint.name}</strong> board. Nobody finds all of them
          first time.
        </p>

        <button type="button" className="btn btn--wide" onClick={onStart}>
          Open the board
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--wide"
          style={{ marginTop: 10 }}
          onClick={onBack}
        >
          Go back
        </button>

        <SiteFooter />
      </div>
    </div>
  );
}
